import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';

const MAX_SIZE_MB = 10;
const ACCEPT = 'image/jpeg,image/png,image/webp';
const CATEGORY = 'before-after';

// Admin tool — Before/After 페어 관리.
// 한 페어 = 작업전 1장 + 작업후 1장 (2026-06-17 단순화). 여러 페어 무제한 등록.
// DDB에는 기존과 동일하게 beforeImageUrls/afterImageUrls 배열(길이 1)로 저장 —
// 데이터 모델 불변. 하위 호환: 기존 다중·단일 페어도 그대로 표시.
export default function BeforeAfterManager() {
  const { getIdToken } = useAuth();
  const [pairs, setPairs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ beforeFile: null, afterFile: null });

  const fetchPairs = useCallback(async () => {
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images?category=${CATEGORY}`);
      const data = await res.json();
      setPairs(data.images || []);
    } catch (err) {
      console.error('[BeforeAfterManager] fetch failed', err);
      setError('페어 목록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => { fetchPairs(); }, [fetchPairs]);

  async function presignAndUpload(file, token) {
    const presign = await fetch(`${AWS_CONFIG.apiUrl}/images/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ category: CATEGORY, filename: file.name, contentType: file.type }),
    });
    if (!presign.ok) throw new Error(`presign ${presign.status}`);
    const { uploadUrl, imageUrl } = await presign.json();
    const put = await fetch(uploadUrl, {
      method: 'PUT', headers: { 'Content-Type': file.type }, body: file,
    });
    if (!put.ok) throw new Error(`S3 ${put.status}`);
    return imageUrl;
  }

  function validateFile(f) {
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      return `${f.name}: ${MAX_SIZE_MB}MB 이하만 가능`;
    }
    return null;
  }

  async function handleAddPair() {
    if (!draft.beforeFile || !draft.afterFile) {
      setError('작업전과 작업후 사진을 각각 1장 선택해주세요.');
      return;
    }
    const sizeErr = validateFile(draft.beforeFile) || validateFile(draft.afterFile);
    if (sizeErr) { setError(sizeErr); return; }
    setError('');
    setBusy('add');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('로그인 세션 만료');
      const beforeUrl = await presignAndUpload(draft.beforeFile, token);
      const afterUrl = await presignAndUpload(draft.afterFile, token);
      const nextSort = pairs.length > 0 ? Math.max(...pairs.map(p => p.sortOrder || 0)) + 1 : 1;
      // 데이터 모델 불변: 단일 파일도 길이 1 배열 + legacy 필드로 함께 저장.
      const body = {
        imageUrl: beforeUrl,
        afterImageUrl: afterUrl,
        beforeImageUrls: [beforeUrl],
        afterImageUrls: [afterUrl],
        alt: `페어 ${nextSort}`,
      };
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${CATEGORY}/${nextSort}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`메타 저장 실패 ${res.status}`);
      setDraft({ beforeFile: null, afterFile: null });
      await fetchPairs();
    } catch (err) {
      setError(`업로드 실패: ${err.message}`);
    }
    setBusy(null);
  }

  async function handleDelete(pair) {
    if (!window.confirm(`"${pair.alt || pair.sortOrder}" 페어를 삭제할까요? 포함된 모든 사진이 함께 삭제됩니다.`)) return;
    setBusy(`del-${pair.sortOrder}`);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${CATEGORY}/${pair.sortOrder}`, {
        method: 'DELETE', headers: { Authorization: token },
      });
      if (!res.ok) throw new Error(`삭제 실패 ${res.status}`);
      await fetchPairs();
    } catch (err) {
      setError(`삭제 실패: ${err.message}`);
    }
    setBusy(null);
  }

  // 새 페어 폼에서 파일 input은 한 장씩 (작업전 1 + 작업후 1).
  function SingleFilePicker({ label, file, onPick }) {
    return (
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: 14, border: '1px dashed var(--border)', borderRadius: 8,
        cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-muted)', minHeight: 80,
        background: file ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
      }}>
        <input
          type="file"
          accept={ACCEPT}
          onChange={(e) => onPick(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
        <strong style={{ color: 'var(--text)' }}>{label}</strong>
        <span>{file ? `✓ ${file.name}` : '클릭하여 사진 선택'}</span>
      </label>
    );
  }

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: '1.1rem', marginBottom: 6, color: 'var(--text-muted)' }}>
        작업전 / 작업후 페어 ({pairs.length}쌍 · 무제한 등록)
      </h2>
      <p style={{ color: 'var(--text-dim)', fontSize: '0.82rem', marginTop: 0, marginBottom: 16 }}>
        한 작업의 작업전 사진 1장과 작업후 사진 1장을 페어로 묶어 등록합니다. <strong style={{ color: 'var(--text)' }}>여러 쌍을 무제한 추가</strong> 가능 — 한 쌍 등록 후 같은 폼으로 계속 추가하세요.
        <br />
        <strong style={{ color: '#ff9800' }}>⚠️ 좌·우로 합친 사진(합성본)이 아니라, 작업전 원본 1장·작업후 원본 1장을 각 칸에 따로 올려주세요.</strong> 홈 화면이 자동으로 왼쪽=작업전 / 오른쪽=작업후로 배치합니다.
      </p>

      {error && (
        <div className="card" style={{
          padding: 12, marginBottom: 16,
          borderColor: '#ff5252', background: 'rgba(255, 82, 82, 0.08)',
          color: '#ff5252', fontSize: '0.9rem',
        }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <SingleFilePicker label="작업전" file={draft.beforeFile}
            onPick={(f) => setDraft(d => ({ ...d, beforeFile: f }))} />
          <SingleFilePicker label="작업후" file={draft.afterFile}
            onPick={(f) => setDraft(d => ({ ...d, afterFile: f }))} />
        </div>
        <button
          className="btn btn-primary"
          onClick={handleAddPair}
          disabled={busy === 'add' || !draft.beforeFile || !draft.afterFile}
          style={{ width: '100%', padding: '10px 18px' }}
        >
          {busy === 'add' ? '업로드 중...' : '+ 페어 추가 (계속 추가 가능)'}
        </button>
      </div>

      <div className="image-manager-grid">
        {pairs.map(p => {
          const before = Array.isArray(p.beforeImageUrls) && p.beforeImageUrls.length > 0
            ? p.beforeImageUrls : (p.imageUrl ? [p.imageUrl] : []);
          const after = Array.isArray(p.afterImageUrls) && p.afterImageUrls.length > 0
            ? p.afterImageUrls : (p.afterImageUrl ? [p.afterImageUrl] : []);
          return (
            <div key={p.sortOrder} className="image-manager-card">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <ThumbStack urls={before} alt="before" />
                <ThumbStack urls={after} alt="after" />
              </div>
              <div className="image-manager-info">
                <div className="image-manager-label">
                  {p.alt || `페어 ${p.sortOrder}`}
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginLeft: 6 }}>
                    (B {before.length}·A {after.length})
                  </span>
                </div>
                <button
                  className="image-manager-btn"
                  disabled={busy === `del-${p.sortOrder}`}
                  onClick={() => handleDelete(p)}
                  style={{ color: '#ff5252', borderColor: '#ff5252' }}
                >
                  {busy === `del-${p.sortOrder}` ? '삭제 중...' : '페어 삭제'}
                </button>
              </div>
            </div>
          );
        })}
        {pairs.length === 0 && (
          <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', padding: '12px 0' }}>
            아직 등록된 페어가 없습니다. 위 폼으로 첫 페어를 추가해주세요.
          </p>
        )}
      </div>
    </div>
  );
}

// 관리자 카드 미리보기에서 한쪽(before 또는 after) 썸네일 스택
function ThumbStack({ urls, alt }) {
  if (urls.length === 0) {
    return (
      <div className="image-manager-preview" style={{
        aspectRatio: '1/1', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-dim)', fontSize: '0.75rem',
      }}>
        없음
      </div>
    );
  }
  if (urls.length === 1) {
    return (
      <div className="image-manager-preview" style={{ aspectRatio: '1/1' }}>
        <img src={urls[0]} alt={alt} />
      </div>
    );
  }
  // 2~3장: 작은 그리드
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, aspectRatio: '1/1' }}>
      {urls.slice(0, 4).map((url, i) => (
        <div key={url} className="image-manager-preview" style={{ aspectRatio: '1/1' }}>
          <img src={url} alt={`${alt}-${i + 1}`} />
        </div>
      ))}
    </div>
  );
}
